import * as React from "react"
import { Check, ChevronDown, Search } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import ReactDOM from "react-dom"

export interface SearchableSelectProps {
  options: { label: string; value: string; description?: string }[]
  value?: string
  onValueChange: (value: string) => void
  placeholder?: string
  className?: string
  id?: string
  disabled?: boolean
  onOpenChange?: (open: boolean) => void
  inDialog?: boolean
  dropdownFooter?: React.ReactNode
  renderOptionAction?: (option: { label: string; value: string }) => React.ReactNode
}

export function SearchableSelect({
  options,
  value,
  onValueChange,
  placeholder = "Select option",
  className,
  id,
  disabled,
  onOpenChange,
  inDialog = false,
  dropdownFooter,
  renderOptionAction,
}: SearchableSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")
  const selected = options.find((option) => option.value === value)
  const searchInputRef = React.useRef<HTMLInputElement>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const [dropdownStyles, setDropdownStyles] = React.useState<React.CSSProperties>({})
  const [inputMounted, setInputMounted] = React.useState(false)

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(search.toLowerCase()) ||
    option.description?.toLowerCase().includes(search.toLowerCase())
  )

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    onOpenChange?.(newOpen)
    if (!newOpen) {
      setSearch("")
      setInputMounted(false)
    } else {
      setTimeout(() => setInputMounted(true), 10)
    }
  }

  // Handle clicking outside to close dropdown
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (
        containerRef.current &&
        dropdownRef.current &&
        !containerRef.current.contains(target) &&
        !dropdownRef.current.contains(target)
      ) {
        handleOpenChange(false)
      }
    }

    if (open) {
      document.addEventListener("mousedown", handleClickOutside)
      return () => document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [open])

  // Handle global keyboard events
  React.useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        handleOpenChange(false)
      }
    }

    if (open) {
      document.addEventListener('keydown', handleGlobalKeyDown)
      return () => document.removeEventListener('keydown', handleGlobalKeyDown)
    }
  }, [open])

  // Focus the search input when dropdown opens
  React.useEffect(() => {
    if (open && inputMounted && searchInputRef.current) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus()
        }
      })
    }
  }, [open, inputMounted])

  // Calculate dropdown position when open — recalculate on scroll/resize
  const updateDropdownPosition = React.useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const dropdownHeight = 300
      const spaceBelow = viewportHeight - rect.bottom
      const showAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight

      setDropdownStyles({
        position: "fixed",
        left: rect.left,
        top: showAbove ? undefined : rect.bottom + 4,
        bottom: showAbove ? (viewportHeight - rect.top + 4) : undefined,
        width: Math.max(rect.width, 250),
        maxHeight: showAbove ? rect.top - 8 : spaceBelow - 8,
        zIndex: inDialog ? 300 : 195,
      } as React.CSSProperties)
    }
  }, [inDialog])

  React.useEffect(() => {
    if (open) {
      updateDropdownPosition()

      const handleReposition = () => updateDropdownPosition()
      window.addEventListener('scroll', handleReposition, true)
      window.addEventListener('resize', handleReposition)
      return () => {
        window.removeEventListener('scroll', handleReposition, true)
        window.removeEventListener('resize', handleReposition)
      }
    }
  }, [open, updateDropdownPosition])

  return (
    <div className={cn("relative w-full", className)} ref={containerRef}>
      <Button
        ref={buttonRef}
        id={id}
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className={cn(
          "w-full justify-between",
          !selected && "text-muted-foreground"
        )}
        disabled={disabled}
        onClick={() => handleOpenChange(!open)}
        type="button"
      >
        {selected ? selected.label : placeholder}
        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {open && ReactDOM.createPortal(
        <div 
          ref={dropdownRef}
          className="bg-popover rounded-md border shadow-md overflow-hidden"
          style={{...dropdownStyles, pointerEvents: 'auto'}}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="p-2">
            <div className="flex items-center border-b mb-2 pb-2">
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                }}
                onKeyDown={(e) => {
                  e.stopPropagation()
                  if (e.key === 'Enter' && filteredOptions.length > 0) {
                    onValueChange(filteredOptions[0].value)
                    handleOpenChange(false)
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation()
                }}
                className="flex-1 bg-transparent border-0 outline-none focus:ring-0 text-sm placeholder:text-muted-foreground text-foreground"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
              />
            </div>
            <div 
              className="max-h-[300px] overflow-y-auto overflow-x-hidden"
              style={{ 
                overscrollBehavior: 'contain',
                WebkitOverflowScrolling: 'touch'
              }}
            >
              {filteredOptions.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">No options found.</div>
              ) : (
                <div className="space-y-1">
                  {filteredOptions.map((option) => (
                    <div
                      key={option.value}
                      onClick={() => {
                        onValueChange(option.value)
                        handleOpenChange(false)
                      }}
                      className={cn(
                        "flex items-start rounded-sm px-3 py-2 text-sm outline-none cursor-pointer text-foreground",
                        "hover:bg-primary/10 hover:text-primary",
                        value === option.value && "bg-primary text-primary-foreground"
                      )}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4 mt-0.5 flex-shrink-0",
                          value === option.value ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                        <span className="break-words">{option.label}</span>
                        {option.description && (
                          <span className="text-xs text-muted-foreground break-words">
                            {option.description}
                          </span>
                        )}
                      </div>
                      {renderOptionAction && (
                        <div className="ml-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                          {renderOptionAction(option)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {dropdownFooter && (
              <div className="border-t p-2">
                {dropdownFooter}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}